import { InputEmail } from 'component-library/index';
import React, {
    Dispatch,
    ReactElement,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import EmailAutoCompleteListBox from './sub-components/EmailAutoCompleteListBox/EmailAutoCompleteListBox';
import './EmailField.css';
import { isValidEmailFormat, wrapTrademarkSuperscript } from '../../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';
import { TFocusEvent, TKeyUpEvent } from '../../../utils/general-types';
import SlideDown from '../../SlideDown/SlideDown';
import InputEmailOrangeAsteriskVariant from '../../../component-library/atoms/InputEmail/Variants/OrangeAsteriskVariant/InputEmailOrangeAsteriskVariant';
import { generateEmailFormatErrorMessage, generateFieldErrorMessage } from '../../../utils/error-message';

interface ISingleString {
    value: string | null;
}
export type TEmailValidationError = 'RequiredField' | 'NotValid' | 'ThirdParty' | undefined;

export interface IEmailFieldProps {
    id?: string | undefined;
    name?: string;
    ref?: React.MutableRefObject<any>;
    inputClass?: string;
    label?: string | React.ReactElement | React.ReactElement[];
    value: string;
    setValueCallback: THandlerFunction;
    valid?: [boolean, Dispatch<SetStateAction<boolean>> | ((arg?: any) => void)];
    showIcon?: boolean | undefined;
    error?: boolean | undefined;
    errorText?: string | undefined;
    helperText?: string | undefined;
    invalidErrorMessage?: string | undefined;
    requiredMessage?: string | undefined;
    alternate?: boolean | undefined;
    readOnly?: boolean | undefined;
    onFocusCallback?: TFocusEvent;
    onBlurCallback?: (event: React.FocusEvent<HTMLInputElement>) => void | Promise<void>;
    validateEmail?: (value: string) => Promise<TEmailValidationError>;
    onValidationComplete?: (value: string, error: TEmailValidationError) => void;
    onKeyUpCallback?: TKeyUpEvent;
    inputChild?: ReactElement | undefined;
    emailAutofill?: ISingleString[];
    emailMessage?: string;
    emailClassName?: string;
    useSuccessIcon?: boolean;
    showSuccessIcon?: boolean;
    removeAsterisk?: boolean;
    orangeAsterisksOnMobile?: boolean;
}

export default function EmailField(
    props: IEmailFieldProps & React.HTMLAttributes<HTMLInputElement>,
) {
    const ref = useRef<HTMLInputElement | null>(props.ref?.current ?? null);

    const [autofillIndex, setAutofillIndex] = useState<number>();
    const showSuggestions = useRef<boolean>(false);
    const [focusedOnce, setFocusedOnce] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<boolean>(false);
    const validationVersion = useRef<number>(0);
    const latestValue = useRef<string>(props.value);
    latestValue.current = props.value;
    const emailRequiredErrorMessage = props.requiredMessage ?? `${generateFieldErrorMessage('Email Address', 'missingValue')}: email@domain.com`;
    const emailFormatErrorMessage = props.invalidErrorMessage ?? generateEmailFormatErrorMessage('Email Address');
    const getExternalErrorMessage = (value: string): string => !value
        ? emailRequiredErrorMessage
        : isValidEmailFormat(value)
            ? generateFieldErrorMessage('Email Address', 'thirdParty', 'Email')
            : emailFormatErrorMessage;
    const [externalErrorMessage, setExternalErrorMessage] = useState<string>(
        getExternalErrorMessage(props.value),
    );

    const filteredEmailsSuffixes: string[] = props.value
        ? props.emailAutofill?.flatMap(({ value }: ISingleString): string[] =>
            value?.includes(props.value.slice(props.value.indexOf('@'))) ? [value] : []) ?? []
        : [];

    const handleFocus = async (e: React.FocusEvent<HTMLInputElement>): Promise<void> => {
        if (e.type === 'blur') {
            // Normalize before starting validation, never during render or after its result.
            const blurredValue = parseAndSetValue(e.currentTarget.value, false);
            const version = ++validationVersion.current;
            setIsValidating(true);
            let error: TEmailValidationError = !blurredValue
                ? 'RequiredField'
                : !isValidEmailFormat(blurredValue) ? 'NotValid' : undefined;
            const blurResult: void | Promise<void> = props.onBlurCallback?.(e);
            if (blurResult) {
                await blurResult;
            }
            if (!error && props.validateEmail) {
                error = await props.validateEmail(blurredValue);
            }
            if (version !== validationVersion.current) return;
            if (latestValue.current !== blurredValue) {
                setIsValidating(false);
                return;
            }
            setExternalErrorMessage(getExternalErrorMessage(blurredValue));
            setValidationError(!!error);
            setIsValidating(false);
            props.valid?.[1](!error);
            props.onValidationComplete?.(blurredValue, error);
        } else if (e.type === 'focus') {
            ++validationVersion.current;
            setIsValidating(false);
            if (!focusedOnce) {
                setFocusedOnce(true);
            }
            setIsFocused(true);

            props.onFocusCallback && props.onFocusCallback(e);
        }
    };

    const parseAndSetValue = (value: string, focused: boolean = isFocused): string => {
        ++validationVersion.current;
        setIsValidating(false);
        const removeInvalidCharacterEntries = (value: string): string => {
            if (!value) {
                return '';
            }
            const spaceRegex = new RegExp(/[\s,=]/g);
            const periodRegex = new RegExp(/\.\./g);
            const hasMultipleAtRegex = new RegExp(/@(.*)@/);
            const LOCAL_ADDRESS_MAX = 64;
            const DOMAIN_MAX = 255;

            while (spaceRegex.test(value) || periodRegex.test(value)) {
                value = value
                    .replace(spaceRegex, '')
                    .replace(periodRegex, '.').trim();
            }

            const hasAt = value.includes('@');

            while (value.length > 0 && value[0] === '.') {
                value = value.replace('.', '');
            }
                
            if (!hasAt && value.length > LOCAL_ADDRESS_MAX) {
                value = value.substring(0, LOCAL_ADDRESS_MAX);
            } else if (hasAt) {
                if (hasMultipleAtRegex.test(value)) {
                    const lastOccurrence = value.lastIndexOf('@');
                    value = value.slice(0, lastOccurrence) + value.slice(lastOccurrence+1);
                }
                if (value.length > DOMAIN_MAX) {
                    const atIndex = value.indexOf('@');
                    value = value.slice(0, atIndex + 1) + value.slice(atIndex + 1, atIndex + 1 + DOMAIN_MAX);
                }
            }
            
            if (value.length > 0 && value[value.length - 1] === '.' && !focused) {
                value = value.substring(0, value.length - 1);
            }

            return value;
        };

        const parsedValue = removeInvalidCharacterEntries(value);
        latestValue.current = parsedValue;
        props.setValueCallback(parsedValue);
        return parsedValue;
    }; 


    const handleKeydown = (e: any) => {
        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                setAutofillIndex(current => {
                    if (current === undefined || current === 0)
                        return filteredEmailsSuffixes!.length - 1;
                    return current - 1;
                });
                break;
            case 'ArrowDown':
                e.preventDefault();
                setAutofillIndex(current => {
                    if (
                        current === undefined ||
                        current === filteredEmailsSuffixes!.length - 1
                    ) {
                        return 0;
                    }
                    return current + 1;
                });
                break;
            case 'Enter':
            case 'Tab':
                if (!isValidEmailFormat(e.target.value)) {
                    e.preventDefault();
                    if (typeof autofillIndex === 'number') {
                        parseAndSetValue(
                            props.value.slice(0, props.value.indexOf('@')) +
                            filteredEmailsSuffixes![autofillIndex],
                        );
                        showSuggestions.current = false;
                    }
                }
                break;
        }
    };

    const handleInput = useCallback((e: any) => {
        if (e.target.value.includes('@')) {
            if (!showSuggestions.current) {
                showSuggestions.current = true;
            }
        } else if (showSuggestions) {
            showSuggestions.current = false;
        }
    }, []);

    useEffect(() => (): void => {
        ++validationVersion.current;
    }, []);

    useEffect(() => {
        if (ref.current) {
            ref.current.setAttribute('autocomplete', 'off');
        }
    }, []);

    useEffect(() => {
        const preventSpaces = (e) => {
            if (e.key === ' ') {
                e.preventDefault();
            }
        };
        if (ref.current) {
            ref.current.addEventListener('input', handleInput);
            ref.current.addEventListener('keydown', preventSpaces);

            return () => {
                ref.current?.removeEventListener('input', handleInput);
                ref.current?.removeEventListener('keydown', preventSpaces);
            };
        }
    }, [focusedOnce]);

    useEffect(() => {
        if (
            filteredEmailsSuffixes &&
            filteredEmailsSuffixes.length > 0 &&
            showSuggestions.current &&
            ref.current
        ) {
            ref.current.addEventListener('keydown', handleKeydown);
            return () => ref.current?.removeEventListener('keydown', handleKeydown);
        }
    });


    return (
        <div className='EmailField'
            onBlur={(e) => {
                const currentTarget = e.currentTarget;
                const relatedTarget = e.relatedTarget;

                if (!currentTarget.contains(relatedTarget as Node)) {
                    setIsFocused(false);
                }
            }}>
            <>
                {!props.orangeAsterisksOnMobile &&              
                <InputEmail
                    useSuccessIcon={props.useSuccessIcon}
                    showSuccessIcon={
                        !isValidating && (props.showSuccessIcon !== undefined
                            ? props.showSuccessIcon
                            : focusedOnce && props.showIcon)
                    }
                    removeAsterisk={props.removeAsterisk}
                    showExclamationError
                    ref={ref}
                    id={props.id}
                    name={props.name}
                    inputClass={props.inputClass}
                    label={props.label}
                    required={true}
                    alternate={props.alternate}
                    value={props.value}
                    externalValidation
                    setValue={(value: string): void => { parseAndSetValue(value); }}
                    helperText={props.helperText}
                    error={!isValidating && (props.error || validationError)}
                    errorText={props.errorText ?? externalErrorMessage}
                    requiredMessage={props.requiredMessage ?? emailRequiredErrorMessage}
                    invalidErrorMessage={props.invalidErrorMessage ?? emailFormatErrorMessage}
                    onFocus={handleFocus}
                    onBlur={handleFocus}
                    readOnly={props.readOnly}
                    maxLength={255}
                    inputChild={
                        props.useSuccessIcon &&
                            isFocused &&
                            showSuggestions.current &&
                            props.emailAutofill ? (
                                <EmailAutoCompleteListBox
                                    className='placedTop'
                                    filteredEmailsSuffixes={filteredEmailsSuffixes ?? []}
                                    currentIndex={autofillIndex}
                                    setCurrentIndex={setAutofillIndex}
                                    setValue={parseAndSetValue}
                                    currentValue={props.value}
                                />
                            ) : undefined
                    }
                />}
                {props.orangeAsterisksOnMobile &&                 
                    <InputEmailOrangeAsteriskVariant
                    useSuccessIcon={props.useSuccessIcon}
                    showSuccessIcon={
                        !isValidating && (props.showSuccessIcon !== undefined
                            ? props.showSuccessIcon
                            : focusedOnce && props.showIcon)
                    }
                    removeAsterisk={props.removeAsterisk}
                    showExclamationError
                    ref={ref}
                    id={props.id}
                    name={props.name}
                    inputClass={props.inputClass}
                    label={props.label}
                    required={true}
                    alternate={props.alternate}
                    value={props.value}
                    externalValidation
                    setValue={(value: string): void => { parseAndSetValue(value); }}
                    helperText={props.helperText}
                    error={!isValidating && (props.error || validationError)}
                    errorText={props.errorText ?? externalErrorMessage}
                    requiredMessage={props.requiredMessage ?? emailRequiredErrorMessage}
                    invalidErrorMessage={props.invalidErrorMessage ?? emailFormatErrorMessage}
                    onFocus={handleFocus}
                    onBlur={handleFocus}
                    readOnly={props.readOnly}
                    maxLength={255}
                    inputChild={
                        props.useSuccessIcon &&
                            isFocused &&
                            showSuggestions.current &&
                            props.emailAutofill ? (
                                <EmailAutoCompleteListBox
                                    className='placedTop'
                                    filteredEmailsSuffixes={filteredEmailsSuffixes ?? []}
                                    currentIndex={autofillIndex}
                                    setCurrentIndex={setAutofillIndex}
                                    setValue={parseAndSetValue}
                                    currentValue={props.value}
                                />
                            ) : undefined
                    }
                />}
                {props.emailMessage && isFocused && (
                    <SlideDown
                        open={isFocused}
                        className={`EmailField__note${focusedOnce ? '' : '--hidden'} ${props.emailClassName ?? ''
                        }`}
                    >
                        {transformHTMLParserLinks(wrapTrademarkSuperscript(props.emailMessage))}
                    </SlideDown>
                )}
            </>
        </div>
    );
}
